/**
 * Menu Actions — porté en fixed pour ne pas être coupé par le tableau.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ActionChevron } from "../../components/ActionChevron";
import { placeFixedMenu, watchMenuPlace } from "../../components/placeMenu";
import "./clientActions.css";
import type { ClientListItem } from "./types";

type ClientActionsProps = {
  client: ClientListItem;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onEdit: () => void;
  onStatement: () => void;
  onArchive: () => void;
  onDelete: () => void;
};

const MENU_W = 184;
const MENU_H = 176;

export function ClientActions({
  client,
  open,
  onToggle,
  onClose,
  onEdit,
  onStatement,
  onArchive,
  onDelete,
}: ClientActionsProps) {
  const box = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 320 });

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = box.current?.querySelector("button")?.getBoundingClientRect();
      if (!r) return;
      setPos(placeFixedMenu(r, MENU_W, menu.current?.scrollHeight || MENU_H));
    };
    const stop = watchMenuPlace(place);
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (box.current?.contains(t) || menu.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      stop();
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open, onClose]);

  return (
    <div className="fx-actions" ref={box}>
      <button type="button" className="fx-actions__btn" aria-expanded={open} onClick={onToggle}>
        Actions <ActionChevron />
      </button>
      {open
        ? createPortal(
            <div
              ref={menu}
              className="fx-actions__menu"
              role="menu"
              style={{ top: pos.top, left: pos.left, maxHeight: pos.maxHeight, overflowY: "auto" }}
            >
              <button type="button" className="fx-actions__item" role="menuitem" onClick={onEdit}>
                Éditer
              </button>
              <button type="button" className="fx-actions__item" role="menuitem" onClick={onStatement}>
                Voir le relevé
              </button>
              <button type="button" className="fx-actions__item" role="menuitem" onClick={onArchive}>
                {client.archived ? "Désarchiver" : "Archiver"}
              </button>
              <button type="button" className="fx-actions__item fx-actions__item--danger" role="menuitem" onClick={onDelete}>
                Supprimer
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
